import { Sparkles } from "lucide-react";
import { cn } from "@/utils/helpers";
import { MONTH_NAMES, yearMonthDots, type CalendarPost } from "../month/calendarModel";
import { calendarState, progressOf, type CalendarRow } from "./calendarIndex";

const HEADS = ["S", "M", "T", "W", "T", "F", "S"];

/** How a day with posts reads on the year (Calendar / Year legend). */
export type YearDotKind = "you" | "client" | "moving" | "live" | "missed";

const DOT_STYLE: Record<YearDotKind, React.CSSProperties> = {
  you: { background: "var(--co-accent-ink)" },
  client: { boxShadow: "inset 0 0 0 1.5px var(--co-text2)" },
  moving: { background: "var(--co-muted)" },
  live: { background: "var(--co-live)" },
  missed: { background: "var(--co-alert-ink)" },
};

/** One 5px dot under a date, or a 7px one in the legend. */
export function YearDot({ kind, size = 5 }: { kind: YearDotKind; size?: 5 | 7 }) {
  return <span aria-hidden="true" className="shrink-0 rounded-full" style={{ width: size, height: size, ...DOT_STYLE[kind] }} />;
}

/** The day's dot: live, missed (a past day that never went live), then whose turn. */
function dotOf(posts: CalendarPost[], past: boolean): YearDotKind | null {
  if (posts.length === 0) return null;
  if (posts.every((post) => post.posted)) return "live";
  if (past) return "missed";
  if (posts.some((post) => post.group === "you")) return "you";
  if (posts.some((post) => post.group === "client")) return "client";
  return "moving";
}

/**
 * Calendar / Year month: a month as weeks of seven cells, each a date and a dot,
 * today circled, with the month's name and state above and one summary line
 * under a hairline ("5 posts · 2 need you", "Plan January", "Plans itself on 5
 * Jan").
 */
export default function YearMonth({
  row,
  posts,
  todayUtc,
  isPast,
  isCurrent,
  showYear,
  autoPlan,
  onOpen,
  onPlan,
  guest = false,
  compact = false,
  tablet = false,
  cellSize,
}: {
  /** A date cell's height in px, set by the year so it fits the screen. */
  cellSize?: number;
  /** Calendar / Year month · Phone: two across a phone, a size down. */
  compact?: boolean;
  /** Tablet (Figma 08, 13 Oct): the head is the month name only; the state leads the foot line. */
  tablet?: boolean;
  /** The client's side: the team's needs-you count is not theirs. */
  guest?: boolean;
  row: CalendarRow;
  /** The month's posts once read; null while only the list's counts are known. */
  posts: CalendarPost[] | null;
  todayUtc: number;
  isPast: boolean;
  isCurrent: boolean;
  /** "January 2027": the year beside the name where the window crosses into it. */
  showYear: boolean;
  /** When an unplanned month plans itself ("5 Dec"), if it will. */
  autoPlan: string | null;
  onOpen?: (() => void) | null;
  onPlan?: (() => void) | null;
}) {
  const state = calendarState(row);
  const name = MONTH_NAMES[row.month - 1];
  const progress = progressOf(row);
  const total = posts?.length ?? progress?.total ?? 0;
  const needYou = posts ? posts.filter((post) => post.group === "you").length : guest ? 0 : (progress?.needsYou ?? 0);
  const live = posts ? posts.filter((post) => post.posted).length : null;
  const closed = state === "delivered" || (isPast && state !== "not_planned");
  const word = isCurrent
    ? "This month"
    : closed
      ? "Closed"
      : state === "planning"
        ? "Planning"
        : state === "failed"
          ? "Planning failed"
          : state === "not_planned"
            ? isPast
              ? "Nothing planned"
              : "Not planned"
            : "In progress";

  const canPlan = !isPast && (state === "not_planned" || state === "failed") && !!onPlan;
  const act = state === "not_planned" || state === "failed" ? (canPlan ? onPlan : null) : onOpen;
  const byDay = new Map<string, CalendarPost[]>();
  for (const post of posts ?? []) {
    const key = post.slot.scheduledAt.slice(0, 10);
    byDay.set(key, [...(byDay.get(key) ?? []), post]);
  }
  // Always six weeks, so the summaries of a row of months line up (Pen).
  const weeks = yearMonthDots(row.year, row.month, posts ?? []);

  const cellHeight = cellSize ?? 36;
  const roomy = compact || cellHeight >= 26;
  const text = compact ? "text-[12.5px]" : "text-[13px]";
  // On a tablet the head holds the name only, so the state word opens the foot line
  // ("Closed · 2 posts", "This month · 3 need you"); the current month keeps its accent name.
  const footWord = tablet ? (
    <span className={cn("whitespace-pre", isCurrent ? "text-co-accent-ink" : "text-co-muted")}>{word} · </span>
  ) : null;
  let summary: React.ReactNode;
  if (state === "failed") {
    summary = <span className={cn(text, "text-co-alert-ink")}>Planning stopped{canPlan ? ". Try again" : ""}</span>;
  } else if (state === "planning") {
    summary = <span className={cn(text, "text-co-accent-ink")}>Planning now</span>;
  } else if (state === "not_planned") {
    summary = canPlan ? (
      <span className={cn(text, "flex flex-wrap items-center gap-x-1.5")}>
        <Sparkles className="size-3.5 shrink-0 text-co-accent" aria-hidden="true" />
        <span className="font-semibold text-co-accent-ink">Plan {name}</span>
        {autoPlan ? <span className="text-co-text2">or it plans itself {autoPlan}</span> : null}
      </span>
    ) : (
      <span className={cn(text, "text-co-muted")}>
        {isPast ? "Nothing was planned" : autoPlan ? `Plans itself on ${autoPlan}` : "Not planned yet"}
      </span>
    );
  } else {
    const missed = closed && live !== null && total > 0 && live < total;
    summary = (
      <span className={cn(text, "flex flex-wrap items-center")}>
        <span className="whitespace-pre text-co-text2">
          {total} {total === 1 ? "post" : "posts"}
          {missed || needYou > 0 ? " · " : ""}
        </span>
        {missed ? (
          <span className="text-co-alert-ink">
            {live === 0 ? (total === 2 ? "neither went live" : total === 1 ? "it did not go live" : "none went live") : `${total - live!} did not go live`}
          </span>
        ) : needYou > 0 ? (
          <span className="text-co-accent-ink">
            {needYou} {needYou === 1 ? "needs" : "need"} you
          </span>
        ) : null}
      </span>
    );
  }

  const body = (
    <>
      <span className="flex items-end gap-2">
        <span
          className={cn(
            "font-section font-bold leading-[1.2]",
            compact ? "text-[18px]" : "text-[20px]",
            isCurrent ? "text-co-accent-ink" : "text-co-text",
          )}
        >
          {name}
          {showYear ? ` ${row.year}` : null}
        </span>
        <span className="flex-1" />
        {compact || tablet ? null : (
          <span className={cn("pb-0.5 text-[12px]", isCurrent ? "text-co-accent-ink" : "text-co-muted")}>{word}</span>
        )}
      </span>
      <span className={cn("grid grid-cols-7 text-center", compact && "gap-y-1.5")} aria-hidden="true">
        {HEADS.map((head, index) => (
          <span
            key={`${head}-${index}`}
            className={cn("text-co-muted", compact ? "pb-0.5 pt-1.5 text-[11px]" : "pb-1 text-[12px]")}
          >
            {head}
          </span>
        ))}
        {weeks.map(({ cell }) => {
          const isToday = cell.inMonth && cell.utc === todayUtc;
          const dayPosts = cell.inMonth ? (byDay.get(cell.key) ?? []) : [];
          const dot = dotOf(dayPosts, cell.utc < todayUtc);
          return (
            <span
              key={cell.key}
              className={cn("flex flex-col items-center", roomy ? "gap-[3px]" : "gap-0.5", compact && "h-[26px] pt-1")}
              // The gap between weeks lives inside the cell, so the cell alone sizes the month.
              style={compact ? undefined : { height: cellHeight, paddingTop: Math.max(0, Math.min(4, cellHeight - 26)) }}
            >
              {!cell.inMonth ? null : isToday ? (
                <span
                  className={cn(
                    "grid place-items-center rounded-full bg-co-accent-fill font-bold leading-none text-co-on-accent tabular-nums",
                    compact ? "-my-px size-5 text-[11px]" : "-my-[3px] size-[22px] text-[12px]",
                  )}
                >
                  {cell.day}
                </span>
              ) : (
                <span
                  className={cn(
                    "tabular-nums",
                    compact ? "text-[12px] leading-[18px]" : "text-[13px] leading-4",
                    dot ? "font-bold text-co-text" : isPast ? "text-co-faint" : "text-co-text2",
                  )}
                >
                  {cell.day}
                </span>
              )}
              {dot && !isToday ? <YearDot kind={dot} /> : null}
            </span>
          );
        })}
      </span>
      <span className="flex min-h-5 flex-wrap items-center pt-1 shadow-[inset_0_1px_0_var(--co-line)]">
        {footWord}
        {summary}
      </span>
    </>
  );

  const label = `${name} ${row.year}. ${word}.${total ? ` ${total} posts.` : ""}${needYou ? ` ${needYou} need you.` : ""}`;
  const className = cn(
    cn("flex min-w-0 flex-col text-left outline-none", compact ? "gap-1.5" : "gap-1"),
    "focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-co-accent",
    act && "group cursor-pointer [&:hover_.font-section]:underline",
  );
  return act ? (
    <button type="button" id={`month-${row.year}-${row.month}`} onClick={act} aria-label={label} className={className}>
      {body}
    </button>
  ) : (
    <div id={`month-${row.year}-${row.month}`} role="group" aria-label={label} className={className}>
      {body}
    </div>
  );
}
